import React, { useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  Box,
  useColorModeValue,
  Heading,
  VStack,
  HStack,
  Flex,
} from '@chakra-ui/react';

const SupplierModals = ({
  isCreateModalOpen,
  isEditModalOpen,
  onCloseCreate,
  onCloseEdit,
  onSubmitCreate,
  onSubmitEdit,
  formData,
  setFormData,
}) => {
  // Color mode values for modern styling
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  
  useEffect(() => {
    if (isCreateModalOpen) {
      // Clear form data when opening "Create New Supplier" modal
      setFormData({ name: '', email: '', phone_number: '', edrpou_code: '', address: '', bank_name: '', bank_number: '' });
    }
  }, [isCreateModalOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <>
      {/* Create Modal */}
      <Modal isOpen={isCreateModalOpen} onClose={onCloseCreate} size="4xl" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent 
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="hidden"
          maxH="80vh"
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="6px"
            bg="linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c)"
          />
          
          <ModalHeader 
            pb={4} 
            pt={8}
            bg={cardBg}
            borderTopRadius="2xl"
          >
            <Heading 
              size="lg"
              bgGradient="linear(to-r, #667eea, #764ba2)"
              bgClip="text"
              fontWeight="bold"
            >
              🏭 Додати постачальника
            </Heading>
          </ModalHeader>
          <ModalCloseButton 
            top={6}
            right={6}
            size="lg"
            borderRadius="full"
            _hover={{
              bg: hoverBg,
              color: textColor,
            }}
          />
          <ModalBody bg={cardBg} pb={6}>
            <Flex gap={8}>
              {/* Left Column */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏢 Назва компанії
                    </FormLabel>
                    <Input 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📧 Email
                    </FormLabel>
                    <Input 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📞 Номер телефону
                    </FormLabel>
                    <Input 
                      name="phone_number" 
                      value={formData.phone_number} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏢 ЄДРПОУ
                    </FormLabel>
                    <Input 
                      name="edrpou_code" 
                      value={formData.edrpou_code} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Right Column */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📍 Адреса
                    </FormLabel>
                    <Input 
                      name="address" 
                      value={formData.address} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏦 Назва банку
                    </FormLabel>
                    <Input 
                      name="bank_name" 
                      value={formData.bank_name} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      💳 Номер рахунку
                    </FormLabel>
                    <Input 
                      name="bank_number" 
                      value={formData.bank_number} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#667eea",
                        boxShadow: "0 0 0 1px rgba(102, 126, 234, 0.3)",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter 
            bg={cardBg}
            borderBottomRadius="2xl"
            p={6}
          >
            <Button 
              bg="linear-gradient(135deg, #667eea, #764ba2)"
              color="white"
              size="lg"
              mr={4} 
              onClick={onSubmitCreate}
              borderRadius="xl"
              px={8}
              boxShadow="0 4px 15px rgba(102, 126, 234, 0.4)"
              _hover={{
                bg: "linear-gradient(135deg, #5a6fd8, #6a4190)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(102, 126, 234, 0.6)",
              }}
              _active={{
                transform: "translateY(0)",
              }}
            >
              🏭 Зберегти
            </Button>
            <Button 
              variant="ghost" 
              onClick={onCloseCreate}
              size="lg"
              borderRadius="xl"
              px={8}
              color={textColor}
              _hover={{
                bg: hoverBg,
              }}
            >
              ❌ Скасувати
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onCloseEdit} size="4xl" isCentered>
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent 
          bg={cardBg}
          borderRadius="2xl"
          border="1px solid"
          borderColor={borderColor}
          boxShadow={`0 20px 25px -5px ${shadowColor}, 0 10px 10px -5px ${shadowColor}`}
          position="relative"
          overflow="hidden"
          maxH="80vh"
        >
          {/* Gradient accent */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            height="6px"
            bg="linear-gradient(90deg, #f093fb, #f5576c, #667eea, #764ba2)"
          />
          
          <ModalHeader 
            pb={4} 
            pt={8}
            bg={cardBg}
            borderTopRadius="2xl"
          >
            <Heading 
              size="lg"
              bgGradient="linear(to-r, #f093fb, #f5576c)"
              bgClip="text"
              fontWeight="bold"
            >
              ✏️ Редагувати постачальника
            </Heading>
          </ModalHeader>
          <ModalCloseButton 
            top={6}
            right={6}
            size="lg"
            borderRadius="full"
            _hover={{
              bg: hoverBg,
              color: textColor,
            }}
          />
          <ModalBody bg={cardBg} pb={6}>
            <Flex gap={8}>
              {/* Left Column */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏢 Назва компанії
                    </FormLabel>
                    <Input 
                      name="name" 
                      value={formData.name} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📧 Email
                    </FormLabel>
                    <Input 
                      name="email" 
                      value={formData.email} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📞 Номер телефону
                    </FormLabel>
                    <Input 
                      name="phone_number" 
                      value={formData.phone_number} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏢 ЄДРПОУ
                    </FormLabel>
                    <Input 
                      name="edrpou_code" 
                      value={formData.edrpou_code} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>

              {/* Right Column */}
              <Box flex="1">
                <VStack spacing={4} align="stretch">
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      📍 Адреса
                    </FormLabel>
                    <Input 
                      name="address" 
                      value={formData.address} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      🏦 Назва банку
                    </FormLabel>
                    <Input 
                      name="bank_name" 
                      value={formData.bank_name} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel 
                      color={textColor} 
                      fontWeight="semibold"
                      mb={3}
                    >
                      💳 Номер рахунку
                    </FormLabel>
                    <Input 
                      name="bank_number" 
                      value={formData.bank_number} 
                      onChange={handleChange}
                      bg={cardBg}
                      border="1px solid"
                      borderColor={borderColor}
                      borderRadius="lg"
                      _focus={{
                        borderColor: "#f093fb",
                        boxShadow: "0 0 0 1px rgba(240, 147, 251, 0.3)",
                      }}
                    />
                  </FormControl>
                </VStack>
              </Box>
            </Flex>
          </ModalBody>
          <ModalFooter 
            bg={cardBg}
            borderBottomRadius="2xl"
            p={6}
          >
            <Button 
              bg="linear-gradient(135deg, #f093fb, #f5576c)"
              color="white"
              size="lg"
              mr={4} 
              onClick={onSubmitEdit}
              borderRadius="xl"
              px={8}
              boxShadow="0 4px 15px rgba(240, 147, 251, 0.4)"
              _hover={{
                bg: "linear-gradient(135deg, #e084fc, #f4466b)",
                transform: "translateY(-2px)",
                boxShadow: "0 8px 25px rgba(240, 147, 251, 0.6)",
              }}
              _active={{
                transform: "translateY(0)",
              }}
            >
              ✏️ Оновити
            </Button>
            <Button 
              variant="ghost" 
              onClick={onCloseEdit}
              size="lg"
              borderRadius="xl"
              px={8}
              color={textColor}
              _hover={{
                bg: hoverBg,
              }}
            >
              ❌ Скасувати
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SupplierModals;
